import { useState } from "react";
import { Button, FormModal, Input, Text } from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import { useCurrentCompany } from "../../lib/company";
import { DOC_TYPE_LABEL, useIssueDocument, type Document } from "../../lib/queries/documents";
import { useNextNumber } from "../../lib/queries/series";
import { formatCivil } from "../../lib/dates";
import { formatMoney } from "../../lib/money";
import styles from "./IssueModal.module.css";

/**
 * Issue a draft (forms design: Vystavení): the number it gets — the next of
 * its series, or a manual one the server checks against the series format
 * and the issue year — and a recap of what becomes final.
 */
export function IssueModal({ doc, onClose }: { doc: Document; onClose: () => void }) {
  const { id } = useCurrentCompany();
  const issue = useIssueDocument(id);
  const year = Number(doc.issue_date.slice(0, 4));
  const next = useNextNumber(id, doc.type, year, true);
  const [manual, setManual] = useState(false);
  const [number, setNumber] = useState("");
  const party = doc.type === "purchase_invoice" ? doc.supplier : doc.customer;
  const end = doc.type === "sales_order" ? doc.expiration_date : doc.due_date;

  return (
    <FormModal
      open
      title={`Vystavit: ${DOC_TYPE_LABEL[doc.type]}`}
      busy={issue.isPending}
      error={issue.error ?? (next.error && !manual ? next.error : undefined)}
      canSubmit={manual ? number.trim() !== "" : !!next.data}
      submitLabel="vystavit"
      onSubmit={async () => {
        await issue.mutateAsync({ id: doc.id, number: manual ? number.trim() : undefined });
        notifySuccess("Doklad byl vystaven.");
        onClose();
      }}
      onClose={onClose}
    >
      <div className={styles.number}>
        <span className={styles.label}>Číslo dokladu</span>
        {manual ? (
          <Input aria-label="Číslo dokladu" value={number} onChange={(e) => setNumber(e.target.value)} hint="Musí odpovídat formátu řady a roku vystavení." autoFocus />
        ) : (
          <span className={styles.value}>{next.data?.number ?? (next.isLoading ? "…" : "—")}</span>
        )}
        <Button type="button" size="sm" weight="ghost" onClick={() => setManual((m) => !m)}>
          {manual ? "další v řadě" : "změnit"}
        </Button>
      </div>
      <dl className={styles.recap}>
        {party && (
          <div>
            <dt>{doc.type === "purchase_invoice" ? "Dodavatel" : "Odběratel"}</dt>
            <dd>{party.business_name}</dd>
          </div>
        )}
        <div>
          <dt>{doc.type === "sales_order" ? "Vystaveno · platnost do" : "Vystaveno · splatnost"}</dt>
          <dd>{formatCivil(doc.issue_date)}{end ? ` · ${formatCivil(end)}` : ""}</dd>
        </div>
        <div>
          <dt>Položek</dt>
          <dd>{doc.items.length}</dd>
        </div>
        <div className={styles.total}>
          <dt>Celkem k úhradě</dt>
          <dd>{formatMoney(doc.total_incl_vat, doc.currency)}</dd>
        </div>
      </dl>
      <Text variant="muted">Vystavený doklad nelze upravit ani smazat; opravuje se dobropisem.</Text>
    </FormModal>
  );
}
